import type { AvimarkCalibrationState } from "@common/lib/api/avimarkCalibration";

export type ExamSetupTone = "ok" | "warn" | "bad" | "muted";
export type ExamSetupAction =
    | "doesnt-look-right"
    | "pick-exam"
    | "pick-exam-with-doctor"
    | "pick-another-exam"
    | "try-again"
    | "check-again"
    | "contact-support"
    | null;

export interface ExamSetupView {
    key: string;
    tone: ExamSetupTone;
    title: string;
    sentence: string;
    action: ExamSetupAction;
    actionLabel: string | null;
}

const LABELS: Record<Exclude<ExamSetupAction, null>, string> = {
    "doesnt-look-right": "Doesn't look right?",
    "pick-exam": "Pick an exam yourself",
    "pick-exam-with-doctor": "Pick an exam with a doctor",
    "pick-another-exam": "Pick another exam",
    "try-again": "Try again",
    "check-again": "Check again",
    "contact-support": "Contact support",
};

const view = (
    key: string,
    tone: ExamSetupTone,
    title: string,
    sentence: string,
    action: ExamSetupAction,
): ExamSetupView => ({
    key,
    tone,
    title,
    sentence,
    action,
    actionLabel: action ? LABELS[action] : null,
});

const KNOWN = new Map<string, ExamSetupView>([
    [
        "no_exam_with_anchor_code",
        view(
            "no_exam_with_anchor_code",
            "warn",
            "Waiting for a first exam",
            "Once someone finishes an exam in AVImark, setup completes on its own.",
            "pick-exam",
        ),
    ],
    [
        "doctor_code_not_copyable",
        view(
            "doctor_code_not_copyable",
            "warn",
            "No exam with a doctor we can copy",
            "We found exams, but none has a doctor assigned that we can copy.",
            "pick-exam-with-doctor",
        ),
    ],
    [
        "no_exam_found",
        view(
            "no_exam_found",
            "warn",
            "No recent exam found",
            "We couldn't find a recent exam to learn from.",
            "pick-exam",
        ),
    ],
    [
        "sample_invalid",
        view(
            "sample_invalid",
            "warn",
            "That exam can't be used",
            "The exam you picked can't be used. Please pick a different one.",
            "pick-another-exam",
        ),
    ],
    [
        "unsupported_avimark_version",
        view(
            "unsupported_avimark_version",
            "muted",
            "Your AVImark version isn't supported yet",
            "Export isn't available for your version of AVImark yet. We'll let you know when it is.",
            null,
        ),
    ],
]);

const CHECKING = view(
    "checking",
    "muted",
    "Checking your AVImark exams",
    "This can take a few minutes, and you can leave this page.",
    null,
);
const OFFLINE = view(
    "offline",
    "muted",
    "HappyBridge is offline",
    "Check that the computer running it is on.",
    "check-again",
);
const TIMED_OUT = view(
    "timed_out",
    "bad",
    "Setup didn't finish",
    "This usually means HappyBridge or AVImark was offline.",
    "try-again",
);

const defaultDate = (iso: string) => new Date(iso).toLocaleDateString();

/** EN-1249 D1: the one place exam setup copy lives (canvas F12). */
export function describeExamSetup(input: {
    state?: AvimarkCalibrationState;
    offline?: boolean;
    formatDate?: (iso: string) => string;
}): ExamSetupView {
    const { state, offline, formatDate = defaultDate } = input;
    if (offline) return OFFLINE;
    if (!state) return CHECKING;
    if (state.effectiveStatus === "checking") return CHECKING;
    // The template still exists, but the replace run after a sample change didn't finish, so exports
    // are refused (avimark_sample_changed). Show why instead of Ready.
    const unresolved = state.sampleChangeUnresolved === true;
    if (!unresolved && (state.effectiveStatus === "done" || state.setupReady)) {
        const at = state.run?.status === "done" ? state.run.finishedAt : null;
        return view(
            "done",
            "ok",
            at ? `Ready, checked ${formatDate(at)}` : "Ready",
            "HappyDoc copies the layout of your clinic's exams.",
            "doesnt-look-right",
        );
    }
    if ((unresolved ? state.run?.status : state.effectiveStatus) === "timed_out") return TIMED_OUT;
    const code = state.run?.reasonCode ?? null;
    const known = code ? KNOWN.get(code) : undefined;
    if (known) return known;
    return view(
        code ?? "unknown",
        "bad",
        "Setup ran into a problem",
        `Contact support and mention code ${code ?? "unknown"}.`,
        "contact-support",
    );
}
